import { ReactNode, useContext } from 'react';

import { Path, StatusLabel } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { NoPage } from '@shared/pages';
import { SignInContext } from '@modules/users';
import { FoldersNavDrawer, isTFolder, TFolder, TPath, useFolder } from '@modules/items';

import { getChangeV2DashboardPath } from '../helpers';

// Extra breadcrumb segments appended after the "Changes" root. The last segment
// is rendered as the current (non-link) crumb by `Path` (hasCurrent).
export type TChangeV2BreadcrumbItem = { name: string; href?: string };

type TProps = {
  breadcrumbTail?: TChangeV2BreadcrumbItem[];
  children: ReactNode;
};

type TInnerProps = {
  qmFolder: TFolder;
  breadcrumbTail: TChangeV2BreadcrumbItem[];
  children: ReactNode;
};

// Changes v2 lives under QM in the nav (company-wide register), so the drawer and
// breadcrumbs are driven by the QM folder - mirroring how literature is scaffolded.
const ChangeV2ScaffoldInner = ({ qmFolder, breadcrumbTail, children }: TInnerProps) => {
  const fullPath: TPath = [
    ...qmFolder.resolvedPath,
    { _id: qmFolder._id, name: qmFolder.name, accessRight: qmFolder.accessRight },
    { _id: 'change-v2', name: 'Changes', accessRight: qmFolder.accessRight, href: getChangeV2DashboardPath() },
    ...breadcrumbTail.map((item, index) => ({
      _id: `change-v2-crumb-${index}`,
      name: item.name,
      accessRight: qmFolder.accessRight,
      href: item.href,
    })),
  ];

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={qmFolder} fullPath={fullPath} selectedValue="change-v2" initialOpen={false} />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ChangeV2PageScaffold = ({ breadcrumbTail = [], children }: TProps) => {
  const { organization } = useContext(SignInContext);
  const qmFolderId = organization.folders.qm;

  const { data, error } = useFolder(qmFolderId, { enabled: Boolean(qmFolderId) });

  if (!qmFolderId) return <NoPage />;

  return (
    <StatusLabel
      data={data}
      error={error}
      loadingMessage="Loading changes..."
      errorMessage="Loading changes failed!"
      render={(folder) =>
        isTFolder(folder) ? (
          <ChangeV2ScaffoldInner qmFolder={folder as TFolder} breadcrumbTail={breadcrumbTail}>
            {children}
          </ChangeV2ScaffoldInner>
        ) : (
          <NoPage />
        )
      }
    />
  );
};

export default ChangeV2PageScaffold;
